import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Calendar, Clock, MapPin, User, ChevronLeft, ChevronRight, Pencil, X, Loader2, Check, UserX } from 'lucide-react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO } from 'date-fns';
import { is } from 'date-fns/locale';
import { useAuthStore } from '@/stores/auth';
import { viewings, agents, type Viewing } from '@/lib/api';
import { formatTime, statusLabels, statusColors, cn } from '@/lib/utils';
import { CreateViewingModal } from '@/components/CreateViewingModal';
import { EditViewingModal } from '@/components/EditViewingModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Viewings() {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingViewing, setEditingViewing] = useState<Viewing | null>(null);
  const [cancellingViewing, setCancellingViewing] = useState<Viewing | null>(null);
  const [completingViewing, setCompletingViewing] = useState<Viewing | null>(null);
  const [noShowViewing, setNoShowViewing] = useState<Viewing | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('all');

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const { data, isLoading } = useQuery({
    queryKey: ['viewings', format(weekStart, 'yyyy-MM-dd')],
    queryFn: () => viewings.list(token!, {
      from: format(weekStart, 'yyyy-MM-dd'),
      to: format(addDays(weekStart, 7), 'yyyy-MM-dd'),
    }),
    enabled: !!token,
  });

  // Fetch agents for filter dropdown
  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agents.list(token!),
    enabled: !!token,
  });

  // Filter viewings by selected agent
  const filteredViewings = useMemo(() => {
    if (!data?.data) return [];
    if (selectedAgentId === 'all') return data.data;
    return data.data.filter((v) => v.agent_id === selectedAgentId);
  }, [data?.data, selectedAgentId]);

  // Cancel viewing mutation
  const cancelMutation = useMutation({
    mutationFn: (viewingId: string) =>
      viewings.update(token!, viewingId, { status: 'aflýst' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viewings'] });
      setCancellingViewing(null);
    },
  });

  // Complete viewing mutation
  const completeMutation = useMutation({
    mutationFn: (viewingId: string) =>
      viewings.update(token!, viewingId, { status: 'lokið' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viewings'] });
      setCompletingViewing(null);
    },
  });

  // No-show viewing mutation
  const noShowMutation = useMutation({
    mutationFn: (viewingId: string) =>
      viewings.update(token!, viewingId, { status: 'kom_ekki' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viewings'] });
      setNoShowViewing(null);
    },
  });

  const getViewingsForDay = (date: Date) => {
    return filteredViewings.filter((v) => isSameDay(parseISO(v.scheduled_at), date));
  };

  const handleEditClick = (viewing: Viewing, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingViewing(viewing);
    setIsEditModalOpen(true);
  };

  const handleCancelClick = (viewing: Viewing, e: React.MouseEvent) => {
    e.stopPropagation();
    setCancellingViewing(viewing);
  };

  const handleConfirmCancel = () => {
    if (cancellingViewing) {
      cancelMutation.mutate(cancellingViewing.id);
    }
  };

  const handleCompleteClick = (viewing: Viewing, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletingViewing(viewing);
  };

  const handleConfirmComplete = () => {
    if (completingViewing) {
      completeMutation.mutate(completingViewing.id);
    }
  };

  const handleNoShowClick = (viewing: Viewing, e: React.MouseEvent) => {
    e.stopPropagation();
    setNoShowViewing(viewing);
  };

  const handleConfirmNoShow = () => {
    if (noShowViewing) {
      noShowMutation.mutate(noShowViewing.id);
    }
  };

  // Check if viewing can be marked as complete or no-show (only scheduled or confirmed)
  const canMarkOutcome = (status: string) => {
    return status === 'bókað' || status === 'staðfest';
  };

  const getViewingStatusClasses = (status: string) => {
    if (status === 'aflýst' || status === 'kom_ekki') {
      return 'bg-gray-100 border border-gray-300 opacity-60';
    }
    if (status === 'bókað') {
      return 'bg-prima-50 border border-prima-200';
    }
    if (status === 'staðfest') {
      return 'bg-blue-50 border border-blue-200';
    }
    if (status === 'lokið') {
      return 'bg-green-50 border border-green-200';
    }
    return 'bg-gray-50 border border-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Skoðanir</h1>
          <p className="text-gray-600 mt-1">
            {filteredViewings.length} skoðanir þessa viku
            {selectedAgentId !== 'all' && data?.data && ` (af ${data.data.length} samtals)`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Agent filter dropdown */}
          <div className="w-48">
            <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
              <SelectTrigger>
                <User className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="Allir söluaðilar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Allir söluaðilar</SelectItem>
                {agentsData?.data?.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            className="btn-primary"
            onClick={() => {
              setSelectedDate(undefined);
              setIsCreateModalOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Bóka skoðun
          </button>
        </div>
      </div>

      {/* Week navigation */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {format(weekStart, 'd. MMMM', { locale: is })} - {format(addDays(weekStart, 6), 'd. MMMM yyyy', { locale: is })}
            </h2>
          </div>
          <button
            onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Week grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          {weekDays.map((day) => {
            const dayViewings = getViewingsForDay(day);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  'card p-4 min-h-[200px] cursor-pointer hover:shadow-md transition-shadow',
                  isToday && 'ring-2 ring-prima-500'
                )}
                onClick={() => {
                  setSelectedDate(day);
                  setIsCreateModalOpen(true);
                }}
              >
                <div className="text-center mb-4">
                  <p className="text-xs text-gray-500 uppercase">
                    {format(day, 'EEEE', { locale: is })}
                  </p>
                  <p className={cn(
                    'text-2xl font-bold',
                    isToday ? 'text-prima-600' : 'text-gray-900'
                  )}>
                    {format(day, 'd')}
                  </p>
                </div>

                <div className="space-y-2">
                  {dayViewings.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center">
                      Engar skoðanir
                    </p>
                  ) : (
                    dayViewings.map((viewing) => (
                      <div
                        key={viewing.id}
                        className={cn(
                          'p-2 rounded-lg text-xs relative group',
                          getViewingStatusClasses(viewing.status)
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Action buttons - show on hover */}
                        {viewing.status !== 'aflýst' && viewing.status !== 'lokið' && viewing.status !== 'kom_ekki' && (
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            {canMarkOutcome(viewing.status) && (
                              <>
                                <button
                                  onClick={(e) => handleCompleteClick(viewing, e)}
                                  className="p-1 hover:bg-green-100 rounded"
                                  title="Lokið"
                                >
                                  <Check className="w-3 h-3 text-green-600" />
                                </button>
                                <button
                                  onClick={(e) => handleNoShowClick(viewing, e)}
                                  className="p-1 hover:bg-red-100 rounded"
                                  title="Kom ekki"
                                >
                                  <UserX className="w-3 h-3 text-orange-600" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={(e) => handleEditClick(viewing, e)}
                              className="p-1 hover:bg-white rounded"
                              title="Breyta"
                            >
                              <Pencil className="w-3 h-3 text-gray-600" />
                            </button>
                            <button
                              onClick={(e) => handleCancelClick(viewing, e)}
                              className="p-1 hover:bg-white rounded"
                              title="Aflýsa"
                            >
                              <X className="w-3 h-3 text-red-600" />
                            </button>
                          </div>
                        )}

                        <div className={cn(
                          'flex items-center gap-1 font-medium mb-1',
                          (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') ? 'text-gray-500 line-through' : 'text-gray-900'
                        )}>
                          <Clock className="w-3 h-3" />
                          {formatTime(viewing.scheduled_at)}
                        </div>
                        <p className={cn(
                          'truncate',
                          (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') ? 'text-gray-500 line-through' : 'text-gray-700'
                        )} title={viewing.property_address}>
                          {viewing.property_address}
                        </p>
                        <p className={cn(
                          'truncate',
                          (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') ? 'text-gray-400' : 'text-gray-500'
                        )}>
                          {viewing.lead_name}
                        </p>
                        {viewing.status === 'aflýst' && (
                          <span className="text-red-500 text-xs font-medium">Aflýst</span>
                        )}
                        {viewing.status === 'lokið' && (
                          <span className="text-green-600 text-xs font-medium">Lokið</span>
                        )}
                        {viewing.status === 'kom_ekki' && (
                          <span className="text-orange-500 text-xs font-medium">Kom ekki</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view for mobile */}
      <div className="md:hidden card divide-y divide-gray-200">
        <div className="px-4 py-3 bg-gray-50">
          <h3 className="font-medium text-gray-900">
            {selectedAgentId === 'all'
              ? 'Allar skoðanir þessa viku'
              : `Skoðanir ${agentsData?.data?.find(a => a.id === selectedAgentId)?.name || ''}`}
          </h3>
        </div>
        {filteredViewings.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Engar skoðanir bókaðar</p>
          </div>
        ) : (
          filteredViewings
            .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())
            .map((viewing) => (
              <div
                key={viewing.id}
                className={cn(
                  'px-4 py-4',
                  (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') && 'bg-gray-50 opacity-60',
                  viewing.status === 'lokið' && 'bg-green-50'
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className={cn(
                      'font-medium',
                      (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') && 'line-through text-gray-500'
                    )}>
                      {format(parseISO(viewing.scheduled_at), 'EEEE d. MMM', { locale: is })}
                    </span>
                    <span className={cn(
                      'font-medium',
                      (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') ? 'text-gray-500 line-through' : 'text-prima-600'
                    )}>
                      {formatTime(viewing.scheduled_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={statusColors[viewing.status] || 'badge-gray'}>
                      {statusLabels[viewing.status] || viewing.status}
                    </span>
                    {viewing.status !== 'aflýst' && viewing.status !== 'lokið' && viewing.status !== 'kom_ekki' && (
                      <div className="flex gap-1">
                        {canMarkOutcome(viewing.status) && (
                          <>
                            <button
                              onClick={(e) => handleCompleteClick(viewing, e)}
                              className="p-1.5 hover:bg-green-100 rounded"
                              title="Lokið"
                            >
                              <Check className="w-4 h-4 text-green-600" />
                            </button>
                            <button
                              onClick={(e) => handleNoShowClick(viewing, e)}
                              className="p-1.5 hover:bg-red-100 rounded"
                              title="Kom ekki"
                            >
                              <UserX className="w-4 h-4 text-orange-600" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => handleEditClick(viewing, e)}
                          className="p-1.5 hover:bg-gray-100 rounded"
                          title="Breyta"
                        >
                          <Pencil className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={(e) => handleCancelClick(viewing, e)}
                          className="p-1.5 hover:bg-gray-100 rounded"
                          title="Aflýsa"
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className={cn(
                  'flex items-center gap-1 text-sm mb-1',
                  (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') ? 'text-gray-500 line-through' : 'text-gray-600'
                )}>
                  <MapPin className="w-4 h-4" />
                  {viewing.property_address}
                </div>
                <div className={cn(
                  'flex items-center gap-4 text-sm',
                  (viewing.status === 'aflýst' || viewing.status === 'kom_ekki') ? 'text-gray-400' : 'text-gray-500'
                )}>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {viewing.lead_name}
                  </span>
                  <span>{viewing.agent_name}</span>
                </div>
              </div>
            ))
        )}
      </div>

      {/* Create Viewing Modal */}
      <CreateViewingModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        defaultDate={selectedDate}
      />

      {/* Edit Viewing Modal */}
      <EditViewingModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        viewing={editingViewing}
      />

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={!!cancellingViewing} onOpenChange={(open) => !open && setCancellingViewing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aflýsa skoðun?</AlertDialogTitle>
            <AlertDialogDescription>
              Ertu viss um að þú viljir aflýsa þessari skoðun?
              {cancellingViewing && (
                <span className="block mt-2 font-medium text-gray-700">
                  {cancellingViewing.property_address} - {format(parseISO(cancellingViewing.scheduled_at), 'd. MMMM yyyy', { locale: is })} kl. {formatTime(cancellingViewing.scheduled_at)}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelMutation.isPending}>
              Hætta við
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={cancelMutation.isPending}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aflýsi...
                </>
              ) : (
                'Aflýsa skoðun'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Complete Viewing Confirmation Dialog */}
      <AlertDialog open={!!completingViewing} onOpenChange={(open) => !open && setCompletingViewing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merkja skoðun sem lokið?</AlertDialogTitle>
            <AlertDialogDescription>
              Staðfesta að skoðunin hafi farið fram?
              {completingViewing && (
                <span className="block mt-2 font-medium text-gray-700">
                  {completingViewing.property_address} - {format(parseISO(completingViewing.scheduled_at), 'd. MMMM yyyy', { locale: is })} kl. {formatTime(completingViewing.scheduled_at)}
                  <br />
                  <span className="text-gray-500">Viðskiptavinur: {completingViewing.lead_name}</span>
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={completeMutation.isPending}>
              Hætta við
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmComplete}
              disabled={completeMutation.isPending}
              className="bg-green-600 hover:bg-green-700 focus:ring-green-500"
            >
              {completeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vista...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Staðfesta
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* No-Show Confirmation Dialog */}
      <AlertDialog open={!!noShowViewing} onOpenChange={(open) => !open && setNoShowViewing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merkja sem mætti ekki?</AlertDialogTitle>
            <AlertDialogDescription>
              Staðfesta að viðskiptavinur hafi ekki mætt í skoðunina?
              {noShowViewing && (
                <span className="block mt-2 font-medium text-gray-700">
                  {noShowViewing.property_address} - {format(parseISO(noShowViewing.scheduled_at), 'd. MMMM yyyy', { locale: is })} kl. {formatTime(noShowViewing.scheduled_at)}
                  <br />
                  <span className="text-gray-500">Viðskiptavinur: {noShowViewing.lead_name}</span>
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={noShowMutation.isPending}>
              Hætta við
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmNoShow}
              disabled={noShowMutation.isPending}
              className="bg-orange-600 hover:bg-orange-700 focus:ring-orange-500"
            >
              {noShowMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vista...
                </>
              ) : (
                <>
                  <UserX className="mr-2 h-4 w-4" />
                  Staðfesta
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
